import * as R from 'ramda';
import React from 'react';

import {translate} from 'iss/strings';
import {PlainAccordionFormSection as PlainSection} from 'iss/widgets/AccordionForm';
import {Button, SubmitButton} from 'iss/widgets/Buttons';
import {translatePartyActivationType} from 'iss/widgets/domainTranslations';
import {Field, FormWithState} from 'iss/widgets/Forms';
import {PartySelector} from 'iss/widgets/Selectors';
import {SelectableContractEditor} from 'iss/widgets/activation/SelectableContractEditor';
import {formatParty} from 'iss/widgets/format/Party';
import {validatorFrom} from 'iss/widgets/form/validation/validators';

export function MultiPartySelectorSection() {
  return <PlainSection id='responsibleParties' validate={[eachActivationHasSiteCoordinator]}
    title={translate('evaluationActivator.responsiblePartiesSectionTitle')} >
    <SelectableContractEditor valuePath="parties" defaultValue={[]}>
      {({getContractValue, selectedValues, updateSelectedValues, ContractSelectorTable}) =>
        <React.Fragment>
          <PartySelectorForm
            onAddParty={(party) => addPartiesToContracts(updateSelectedValues, party)}
            onClear={() => updateSelectedValues(R.always([]))}
            selectedParties={selectedValues} />
          <ContractSelectorTable id="contractParties"
            columns={[
              {
                header: translate('evaluationActivator.responsiblePartiesSectionTitle'),
                accessor: (contract) => buildResponsiblePartiesString(getContractValue(contract))
              }
            ]} />
        </React.Fragment>
      }
    </SelectableContractEditor>
  </PlainSection>;
}

function toActivationPartyArray(parties, activationPartyType) {
  return (parties || []).map(p => ({activationPartyType: activationPartyType, party: p}));
}

function addPartiesToContracts(updateSelectedValues, party) {
  if (party.xparties == undefined) {
    return;
  }

  const informants = toActivationPartyArray(party.xparties.informants, 'INFORMANT');
  const coordinators = toActivationPartyArray(party.xparties.siteCoordinators, 'SITE_COORDINATOR');

  const newParties = [...coordinators, ...informants, ];
  delete party.xparties;

  updateSelectedValues(value => {
    const partiesWithoutDuplicates = R.uniqWith((a, b) => a.party.id == b.party.id, value.concat(newParties));
    return partiesWithoutDuplicates;
  });
}

function buildResponsiblePartiesString(contractValue) {
  const coordinators = contractValue.filter(value => value.activationPartyType === 'SITE_COORDINATOR').map(showParty).join(", ");
  const informants = contractValue.filter(value => value.activationPartyType === 'INFORMANT').map(showParty).join(", ");
  const coordinatorsText = coordinators ? translatePartyActivationType('SITE_COORDINATOR') + ": " + coordinators : "";
  const informantsText = informants ? translatePartyActivationType('INFORMANT') + ": " + informants : "";
  const isSeparatorNeeded = coordinatorsText !== "" && informantsText !== "";
  const divider = isSeparatorNeeded ? " - " : "";
  return coordinatorsText + divider + informantsText;
}

function showParty({party}) {
  return formatParty(party, "short");
}

const eachActivationHasSiteCoordinator = validatorFrom(
  ({parties, contracts}) => R.all(contract => containsSiteCoordinator(parties[contract.id] || []), contracts),
  "batchEvaluationActivator.needsToHaveSiteCoordinators"
);
const containsSiteCoordinator = R.any(R.propEq("SITE_COORDINATOR", "activationPartyType"));

let formRevision = 1;

// selectedParties = array of contract specific arrays containing {activationPartyType, party} objects.
function PartySelectorForm({selectedParties, onAddParty, onClear}) {
  const hasContracts = selectedParties.length > 0;
  const selectedPartyObjs = selectedParties.reduce(R.concat, []).map(obj => obj.party);

  // Use formRevision as a key to force resetting PartySelectorsGroup state when selected
  // parties are added to selected contracts. In that case also the party type specific fields
  // are cleared. Perhaps there is some way to associate PartySelectorsGroup's state with
  // the form state but cannot figure out so use a key instead :|
  return <FormWithState onSubmit={party => { formRevision += 1; onAddParty(party); }} id="addParty"
    initialValues={{}}>

    <PartySelectorsGroup partiesForContracts={selectedPartyObjs} key={formRevision}/>

    <Button onClick={onClear} id="clearParties" buttonLook="danger" disabled={!hasContracts}>
      {translate('batchEvaluationActivator.clearSelected')}
    </Button>
    <Field valuePath="xparties">
      {partiesField =>
        <SubmitButton id="addParties" disabled={!hasContracts || flattenParties(partiesField.getValue()).length == 0}>
          {translate('batchEvaluationActivator.addToSelected')}
        </SubmitButton>
      }
    </Field>
  </FormWithState>;
}

// A separate component is declared so that excludedParties is updated as user adds or removes parties
// in any of the type specific fields. In addition to the parties currently in the type specific fields
// one also needs to exclude the parties already added to the currently selected contracts
// (these are defined in property partiesForContracts which is an array of party objects).
//
// Not that this component is supposed to be reused elsewhere, but since the PartySelectors define a
// valuePath this probably must be used inside a Form.
class PartySelectorsGroup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {selectedParties: {}};
  }
  render() {
    const excludedParties = flattenParties(this.state.selectedParties).concat(this.props.partiesForContracts || []);

    return (
      <div>
        <PartySelector label="evaluationActivator.siteCoordinators" multi={true} excludedParties={excludedParties}
          id="siteCoordinators" valuePath="xparties.siteCoordinators"
          onChange={selected => this.setState(state => state.selectedParties['siteCoordinators'] = selected)}
          includeEmailOnlyUsers={false} />

        <PartySelector creatable label="evaluationActivator.informants" multi={true} excludedParties={excludedParties}
          id="informants" valuePath="xparties.informants"
          onChange={selected => this.setState(state => state.selectedParties['informants'] = selected)}
          includeEmailOnlyUsers={true} />
      </div>
    );
  }
}

// Flatten object with type specific party arrays into a single array of parties.
function flattenParties(parties) {
  return R.values(parties).reduce(R.concat, []);
}

export function getActivationParties(contract, parties) {
  return (parties[contract.id] || []).map(({party, activationPartyType}) => ({
    id: party.id,
    activationType: activationPartyType
  }));
}



// WEBPACK FOOTER //
// ./modules/issFrontend/widgets/activation/MultiPartySelectorSection.jsx